import type { AgeBand, ChildWithProfile } from '@kidshub/contracts';
import { type FormEvent, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../i18n/use-locale.js';
import { apiClient } from '../lib/api-client.js';
import { toMinutes } from '../lib/duration.js';
import { toErrorKey } from '../lib/error-code.js';
import { toInitial } from '../lib/turkish-case.js';
import { useResource } from '../lib/use-resource.js';
import { AppShell } from '../ui/app-shell.js';
import { Button, ButtonLink, Chip, EmptyState, Segmented } from '../ui/kit.js';
import { AGE_BANDS, AVATAR_KEYS, type AvatarKey, DEFAULT_AVATAR_KEY } from './avatar-keys.js';
import '../screen-time/child.css';

/**
 * Children, and the form that adds one.
 *
 * A new child arrives from the server already usable — a seven-day schedule and
 * an age-appropriate budget. That is deliberate on the contract's side and this
 * screen must not undo it by demanding a setup wizard first: an empty schedule
 * denies everything (`SPEC.md §17`), so a child created without one could open
 * nothing and the parent would reasonably conclude the product was broken.
 *
 * Each child's settings, languages included, live on that child's own page;
 * this list says enough to tell the children apart and links there.
 */
export const ChildrenPage = () => {
  const { t } = useTranslation();
  const locale = useLocale();
  const nameId = useId();
  const { resource, reload } = useResource('children', () => apiClient.children.list());

  const [name, setName] = useState('');
  const [ageBand, setAgeBand] = useState<AgeBand>('TODDLER');
  const [avatarKey, setAvatarKey] = useState<AvatarKey>(DEFAULT_AVATAR_KEY);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setErrorKey(null);
    setIsSubmitting(true);

    try {
      await apiClient.children.create({ name, ageBand, avatarKey });
      setName('');
      reload();
    } catch (error) {
      setErrorKey(toErrorKey(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppShell title={t('children.title')} subtitle={t('children.subtitle')}>
      <div className="children-layout">
        <section aria-label={t('children.title')}>
          {resource.status === 'loading' ? <p className="t-meta">{t('common.loading')}</p> : null}

          {resource.status === 'failed' ? (
            <p className="help err" role="alert">
              {t(toErrorKey(resource.error))}
            </p>
          ) : null}

          {resource.status === 'ready' && resource.data.children.length === 0 ? (
            <EmptyState title={t('children.empty')} detail={t('child.emptyDetail')} />
          ) : null}

          {resource.status === 'ready' && resource.data.children.length > 0 ? (
            <ul className="child-list">
              {resource.data.children.map((entry) => (
                <ChildItem
                  key={entry.child.id}
                  entry={entry}
                  initial={toInitial(entry.child.name, locale)}
                />
              ))}
            </ul>
          ) : null}
        </section>

        <form className="card add-child" onSubmit={(event) => void submit(event)}>
          <h2 className="t-h2">{t('children.addTitle')}</h2>

          <div className="field">
            <label htmlFor={nameId}>{t('children.name')}</label>
            <input
              id={nameId}
              className="input"
              type="text"
              required
              maxLength={40}
              autoComplete="off"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>

          <div className="field">
            <span className="field-label">{t('children.ageBand')}</span>
            <Segmented
              label={t('children.ageBand')}
              value={ageBand}
              options={AGE_BANDS.map((band) => ({ value: band, label: t(`ageBand.${band}`) }))}
              onChange={setAgeBand}
            />
          </div>

          <fieldset className="field">
            <legend className="field-label">{t('children.avatar')}</legend>
            <div className="chips">
              {AVATAR_KEYS.map((key) => (
                <Chip key={key} selected={key === avatarKey} onToggle={() => setAvatarKey(key)}>
                  {t(`avatar.${key}`)}
                </Chip>
              ))}
            </div>
          </fieldset>

          <p className="help">{t('children.defaultsNote')}</p>

          {errorKey === null ? null : (
            <p className="help err" role="alert">
              {t(errorKey)}
            </p>
          )}

          <Button type="submit" variant="primary" block disabled={isSubmitting}>
            {t(isSubmitting ? 'children.adding' : 'children.add')}
          </Button>
        </form>
      </div>
    </AppShell>
  );
};

const ChildItem = ({ entry, initial }: { entry: ChildWithProfile; initial: string }) => {
  const { t } = useTranslation();
  const { child, profile } = entry;

  return (
    <li className="card child-item">
      <span className="avatar" aria-hidden="true">
        {initial}
      </span>
      <div className="child-item-text">
        <b className="t-title">{child.name}</b>
        <span className="t-meta">
          {t('child.listMeta', {
            ageBand: t(`ageBand.${child.ageBand}`),
            budget: t('children.dailyBudget', {
              minutes: toMinutes(profile.budget.dailyTotalSeconds),
            }),
            language: t(`language.${profile.nativeLanguage}`),
          })}
        </span>
      </div>
      <ButtonLink to={`/children/${child.id}/screen-time`} variant="secondary" small>
        {t('children.editRules')}
      </ButtonLink>
    </li>
  );
};
